import React, { useState, useEffect } from 'react';
import { Download, FileText } from 'lucide-react';
import { reportAPI } from '../utils/api';
import '../styles/Reports.css';

function Reports() {
  const [reportType, setReportType] = useState('gst');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      alert('Please select date range');
      return;
    }

    try {
      setLoading(true);
      let response;

      if (reportType === 'gst') {
        response = await reportAPI.getGSTReport(fromDate, toDate);
      } else if (reportType === 'non-gst') {
        response = await reportAPI.getNonGSTReport(fromDate, toDate);
      } else if (reportType === 'daily') {
        response = await reportAPI.getDailySalesReport(fromDate);
      } else if (reportType === 'sales-register') {
        response = await reportAPI.getSalesRegister(fromDate, toDate);
      } else if (reportType === 'purchase-report') {
        response = await reportAPI.getPurchaseReport(fromDate, toDate);
      }

      setReportData(response.data);
    } catch (error) {
      alert('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    const csvContent = convertToCSV(reportData, reportType);
    downloadCSV(csvContent, `Report-${reportType}.csv`);
  };

  const convertToCSV = (data, type) => {
    if (!data || data.length === 0) return '';

    let headers = [];
    let rows = [];

    if (type === 'sales-register') {
      headers = ['Date', 'Invoice No', 'Customer', 'GSTIN', 'State', 'Quantity', 'Gross Amount', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Tax', 'Grand Total'];
      rows = data.map((r) => [
        new Date(r.invoiceDate).toLocaleDateString(),
        r.invoiceNumber,
        r.customerName,
        r.gstinNumber || '',
        r.state || '',
        r.totalQuantity,
        Number(r.grossAmount || 0).toFixed(2),
        Number(r.taxableValue || 0).toFixed(2),
        Number(r.cgstAmount || 0).toFixed(2),
        Number(r.sgstAmount || 0).toFixed(2),
        Number(r.igstAmount || 0).toFixed(2),
        Number(r.totalTax || 0).toFixed(2),
        Number(r.grandTotal || 0).toFixed(2)
      ]);
    } else if (type === 'gst') {
      headers = ['HSN Code', 'Product Name', 'Total Quantity', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total GST', 'Grand Total'];
      rows = data.map((r) => [
        r.hsnCode,
        r.productName,
        r.totalQuantity,
        Number(r.taxableValue || 0).toFixed(2),
        Number(r.cgstAmount || 0).toFixed(2),
        Number(r.sgstAmount || 0).toFixed(2),
        Number(r.igstAmount || 0).toFixed(2),
        Number(r.totalGst || 0).toFixed(2),
        Number(r.grandTotal || 0).toFixed(2)
      ]);
    } else if (type === 'non-gst') {
      headers = ['Product Name', 'Total Quantity', 'Total Sales Amount', 'Category'];
      rows = data.map((r) => [
        r.productName,
        r.totalQuantity,
        Number(r.totalSalesAmount || 0).toFixed(2),
        r.category
      ]);
    } else if (type === 'daily') {
      headers = ['Invoice No', 'Date', 'Customer', 'Quantity', 'Grand Total'];
      rows = data.map((r) => [
        r.invoiceNumber,
        new Date(r.invoiceDate).toLocaleDateString(),
        r.fullName,
        r.quantity,
        Number(r.grandTotal || 0).toFixed(2)
      ]);
    } else if (type === 'purchase-report') {
      headers = ['Date', 'Purchase No', 'Supplier', 'GSTIN', 'Quantity', 'Subtotal', 'Tax', 'Grand Total', 'Payment Mode'];
      rows = data.map((r) => [
        new Date(r.purchaseDate).toLocaleDateString(),
        r.purchaseNumber,
        r.supplierName,
        r.supplierGSTIN || '',
        r.totalQuantity,
        Number(r.subtotal || 0).toFixed(2),
        Number(r.totalTax || 0).toFixed(2),
        Number(r.grandTotal || 0).toFixed(2),
        r.paymentMode
      ]);
    }

    const escape = (val) => `"${String(val || '').replace(/"/g, '""')}"`;
    const csvRows = rows.map((row) => row.map(escape).join(','));
    return [headers.join(','), ...csvRows].join('\n');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  };

  const handlePrintReport = () => {
    window.print();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reports & Analytics</h1>
      </div>

      <div className="card">
        <form onSubmit={handleGenerateReport} className="report-filter">
          <div className="form-row">
            <div className="form-group">
              <label>Report Type</label>
              <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
                <option value="gst">Monthly GST Report (HSN-wise)</option>
                <option value="non-gst">Monthly Non-GST Report</option>
                <option value="daily">Daily Sales Report</option>
                <option value="sales-register">Sales Register (Invoice-wise)</option>
                <option value="purchase-report">Purchase Report</option>
              </select>
            </div>

            <div className="form-group">
              <label>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                required
              />
            </div>

            {reportType !== 'daily' && (
              <div className="form-group">
                <label>To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <button type="submit" className="btn btn-primary">
                Generate Report
              </button>
            </div>
          </div>
        </form>
      </div>

      {reportData.length > 0 && (
        <div className="card report-section">
          <div className="report-header">
            <h2>
              {reportType === 'gst' && 'Monthly GST Report (HSN-wise)'}
              {reportType === 'non-gst' && 'Monthly Non-GST Report'}
              {reportType === 'daily' && 'Daily Sales Report'}
              {reportType === 'sales-register' && 'Sales Register (Invoice-wise)'}
              {reportType === 'purchase-report' && 'Purchase Report'}
            </h2>
            <div className="report-actions">
              <button className="btn btn-secondary" onClick={handlePrintReport}>
                <FileText size={16} />
                Print
              </button>
              <button className="btn btn-secondary" onClick={downloadExcel}>
                <Download size={16} />
                Export CSV
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    {reportType === 'gst' && (
                      <>
                        <th>HSN Code</th>
                        <th>Product Name</th>
                        <th>Total Quantity</th>
                        <th>Taxable Value</th>
                        <th>CGST</th>
                        <th>SGST</th>
                        <th>IGST</th>
                        <th>Total GST</th>
                        <th>Grand Total</th>
                      </>
                    )}
                    {reportType === 'non-gst' && (
                      <>
                        <th>Product Name</th>
                        <th>Total Quantity</th>
                        <th>Total Sales Amount</th>
                        <th>Category</th>
                      </>
                    )}
                    {reportType === 'daily' && (
                      <>
                        <th>Invoice No</th>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Quantity</th>
                        <th>Grand Total</th>
                      </>
                    )}
                    {reportType === 'sales-register' && (
                      <>
                        <th>Date</th>
                        <th>Invoice No</th>
                        <th>Customer</th>
                        <th>GSTIN</th>
                        <th>State</th>
                        <th>Quantity</th>
                        <th>Gross Amount</th>
                        <th>Taxable Value</th>
                        <th>CGST</th>
                        <th>SGST</th>
                        <th>IGST</th>
                        <th>Total Tax</th>
                        <th>Grand Total</th>
                      </>
                    )}
                    {reportType === 'purchase-report' && (
                      <>
                        <th>Date</th>
                        <th>Purchase No</th>
                        <th>Supplier</th>
                        <th>GSTIN</th>
                        <th>Quantity</th>
                        <th>Subtotal</th>
                        <th>Tax</th>
                        <th>Grand Total</th>
                        <th>Payment Mode</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, idx) => (
                    <tr key={idx}>
                      {reportType === 'gst' && (
                        <>
                          <td>{row.hsnCode}</td>
                          <td>{row.productName}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.taxableValue).toFixed(2)}</td>
                          <td>₹{parseFloat(row.cgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.sgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.igstAmount || 0).toFixed(2)}</td>
                          <td className="total-cell">
                            ₹{parseFloat(row.totalGst || 0).toFixed(2)}
                          </td>
                          <td className="grand-total-cell">
                            ₹{parseFloat(row.grandTotal || 0).toFixed(2)}
                          </td>
                        </>
                      )}
                      {reportType === 'non-gst' && (
                        <>
                          <td>{row.productName}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.totalSalesAmount).toFixed(2)}</td>
                          <td>{row.category}</td>
                        </>
                      )}
                      {reportType === 'daily' && (
                        <>
                          <td>{row.invoiceNumber}</td>
                          <td>{new Date(row.invoiceDate).toLocaleDateString()}</td>
                          <td>{row.fullName}</td>
                          <td>{row.quantity}</td>
                          <td>₹{parseFloat(row.grandTotal).toFixed(2)}</td>
                        </>
                      )}
                      {reportType === 'sales-register' && (
                        <>
                          <td>{new Date(row.invoiceDate).toLocaleDateString()}</td>
                          <td>{row.invoiceNumber}</td>
                          <td>{row.customerName}</td>
                          <td>{row.gstinNumber}</td>
                          <td>{row.state}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.grossAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.taxableValue || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.cgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.sgstAmount || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.igstAmount || 0).toFixed(2)}</td>
                          <td className="total-cell">₹{parseFloat(row.totalTax || 0).toFixed(2)}</td>
                          <td className="grand-total-cell">₹{parseFloat(row.grandTotal || 0).toFixed(2)}</td>
                        </>
                      )}
                      {reportType === 'purchase-report' && (
                        <>
                          <td>{new Date(row.purchaseDate).toLocaleDateString()}</td>
                          <td>{row.purchaseNumber}</td>
                          <td>{row.supplierName}</td>
                          <td>{row.supplierGSTIN || '-'}</td>
                          <td>{row.totalQuantity}</td>
                          <td>₹{parseFloat(row.subtotal || 0).toFixed(2)}</td>
                          <td>₹{parseFloat(row.totalTax || 0).toFixed(2)}</td>
                          <td className="grand-total-cell">₹{parseFloat(row.grandTotal || 0).toFixed(2)}</td>
                          <td>{row.paymentMode}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Reports;
